/**
 * TopNav Component - Genre filtering and user controls
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Search, User, Radio, MessageCircle, Disc3, ArrowRight } from 'lucide-react';
import { cn } from '../utils/helpers';
import { messagesApi } from '../api/messages';
import NotificationBell from './NotificationBell';

const TopNav = ({ genres = [], selectedGenre, onGenreChange, user, isAuthenticated }) => {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread message count
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const fetchUnread = async () => {
      try {
        const data = await messagesApi.getUnreadCount();
        setUnreadCount(data.count || 0);
      } catch (err) {
        // Silently fail
      }
    };

    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="fixed top-0 left-0 right-0 z-50"
      data-testid="top-nav"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#050505]/80 to-transparent pointer-events-none" />

      <div className="relative px-4 py-4">
        {/* Top Row */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <img 
              src="/nlt-radio-logo.png" 
              alt="NLT Radio" 
              className="w-10 h-10 rounded-xl object-contain"
            />
            <div>
              <h1 className="text-white font-bold text-lg tracking-tight" style={{ fontFamily: 'Unbounded' }}>NLT Radio</h1>
              <p className="text-[#52525B] text-xs -mt-0.5">Live Rooms</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/releases')}
              className="flex items-center gap-1.5 h-10 px-3 rounded-full bg-white/10 backdrop-blur-sm hover:bg-white/20 transition-colors"
              data-testid="coming-soon-btn"
              title="Coming Soon"
            >
              <Disc3 className="w-4 h-4 text-[#FF007F]" />
              <span className="hidden sm:inline text-white text-xs font-semibold">Coming Soon</span>
            </button>
            <button 
              onClick={() => navigate('/discover')}
              className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors" 
              data-testid="search-btn"
            >
              <Search className="w-5 h-5 text-white" />
            </button>
            
            {isAuthenticated && <NotificationBell />}

            {isAuthenticated && (
              <button 
                onClick={() => navigate('/messages')}
                className="relative w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors" 
                data-testid="messages-btn"
              >
                <MessageCircle className="w-5 h-5 text-white" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF007F] rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            )}
            
            {isAuthenticated ? (
              <button 
                onClick={() => navigate('/profile')}
                className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/20 hover:border-[#00F0FF] transition-colors" 
                data-testid="user-menu-btn"
              >
                {user?.profilePhoto || user?.googleProfilePicture ? (
                  <img src={user.profilePhoto || user.googleProfilePicture} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-[#12101A] to-[#1a1625] flex items-center justify-center">
                    <User className="w-5 h-5 text-white/70" />
                  </div>
                )}
              </button>
            ) : (
              <button 
                onClick={() => navigate('/auth')}
                className="px-4 py-2 rounded-full bg-[#00F0FF] text-[#050505] font-medium text-sm hover:bg-[#00F0FF]/90 transition-colors" 
                data-testid="login-btn"
              >
                Sign In
              </button>
            )}
          </div>
        </div>

        {/* Genre Pills */}
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {genres.map((genre) => (
            <React.Fragment key={genre.id}>
              <button
                onClick={() => onGenreChange(genre.id)}
                data-testid={`genre-${genre.id}`}
                className={cn(
                  "px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200",
                  selectedGenre === genre.id
                    ? "bg-white text-[#050505] shadow-[0_0_20px_rgba(255,255,255,0.2)]"
                    : "bg-white/10 text-white/70 hover:bg-white/20 hover:text-white"
                )}
                style={selectedGenre === genre.id ? { boxShadow: `0 0 20px ${genre.color}40` } : {}}
              >
                {genre.name}
              </button>

              {/* Inline "Hub →" pill for the active, non-"all" genre */}
              {selectedGenre === genre.id && genre.id !== 'all' && (
                <button
                  onClick={() => navigate(`/genre/${genre.id}`)}
                  data-testid={`genre-hub-${genre.id}`}
                  title={`Open ${genre.name} Hub`}
                  className="px-3 py-2 rounded-full text-sm font-semibold whitespace-nowrap bg-white/10 text-white hover:bg-white/20 transition-colors flex items-center gap-1"
                  style={{ boxShadow: `0 0 16px ${genre.color}33`, border: `1px solid ${genre.color}66` }}
                >
                  Hub <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </motion.nav>
  );
};

export default TopNav;
